import { isEmpty } from 'lodash';
import { Dialog, IconButton } from '@mui/material';
import Countdown from 'react-countdown';
import {
  Close, LockOpen, Lock, Celebration,
} from '@mui/icons-material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ProfileUser } from 'lib/types/users';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useEffect, useState } from 'react';
import { AwaitUserAuth } from 'components/AwaitUserAuth';
import { trackClickSignupEvent } from 'lib/hooks/useEventTracking';
import { replayAssetUrl } from 'lib/utils/imageUtils';
import { VIDEO_QUALITY } from 'lib/constants/replay';

interface Props {
  profileUser?: ProfileUser;
  setCanAccessReplay: (arg: boolean) => void;
}

export const LAUNCH_DATE = new Date('2026-01-07T08:30:00-0500');
export const DATE = new Date();

export const ReplayCountdown = ({ profileUser, setCanAccessReplay }: Props) => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const [isLaunched, setIsLaunched] = useState(DATE > LAUNCH_DATE);
  const API = useApi();

  useEffect(() => {
    if (isLaunched && currentUser?.id) {
      setCanAccessReplay(true);
    }

    const timer = setInterval(() => setIsLaunched(new Date() > LAUNCH_DATE), 1000);
    return function cleanup() {
      clearInterval(timer);
    };
  }, [currentUser, isLaunched, setCanAccessReplay]);

  const signInButtons = (text: string) => (
    <div>
      <div className="">
        <Link href={`/signin?returnTo=${router.asPath}`} passHref>
          <button type="button" className="btn-primary-outlined normal-case rounded-lg text-xl font-bold py-2 w-full max-w-xs">
            <Lock fontSize="large" />
            <span className="mx-2">
              {text}
            </span>
          </button>
        </Link>
      </div>
      <div className="text-lg my-4">
        <span className="font-semibold">Don&apos;t have a Gondola account?</span>
        {' '}
        <Link href={`/signup?returnTo=${router.asPath}`} onClick={() => trackClickSignupEvent(API, 'replay2025')} passHref>
          <span className="underline">
            Sign Up
          </span>
        </Link>
      </div>
    </div>
  );

  const countdownRenderer = ({
    days, hours, minutes, seconds,
  }: any) => (
    <div className="text-3xl mb-4">
      <div>
        <b>{days}</b>
        {' '}
        days
      </div>
      <div>
        <b>{hours}</b>
        {' '}
        hours
      </div>
      <div>
        <b>{minutes}</b>
        {' '}
        minutes
      </div>
      <div>
        <b>{seconds}</b>
        {' '}
        seconds
      </div>
    </div>
  );

  const signUpPro = (text: string) => (
    <>
      <div className="mb-2 flex items-center gap-2">
        <Lock fontSize="large" />
        {text}
      </div>
      <Link legacyBehavior href="/plans" passHref>
        <a href="/plans" className="underline">
          <button type="button" className="w-fit self-end btn-primary">
            <span className="mx-2">Let&apos;s go pro!</span>
            <span role="img" aria-label="strong">💪</span>
          </button>
        </a>
      </Link>
    </>
  );

  const countingDown = () => (
    <div>
      <div className="text-2xl md:text-3xl mb-2">Replay launching in:</div>
      <Countdown date={LAUNCH_DATE} renderer={countdownRenderer} />
      {(profileUser?.isPro || currentUser?.isAdmin) ? (
        <div className="text-xl">
          <div className="mb-2 flex items-center gap-2">
            <LockOpen fontSize="large" />
            You&apos;re a PRO, enjoy your early access!
          </div>
          <button type="button" className="btn-primary" onClick={() => setCanAccessReplay(true)}>Start replay</button>
        </div>
      ) : (
        <div className="text-xl">
          {(!isEmpty(profileUser)) ? signUpPro('Sign up for PRO to get early access!') : signInButtons('Login to get access')}
        </div>
      )}
    </div>
  );

  const countdownComplete = () => (
    <div className="text-2xl py-4">
      <div className="mb-4 flex items-center gap-2">
        <Celebration fontSize="large" />
        Replay 2025 is here!
      </div>
      {!currentUser
        ? signInButtons('Login to access Replay')
        : <button type="button" className="btn-primary" onClick={() => setCanAccessReplay(true)}>Start replay</button>}
    </div>
  );

  return (
    <AwaitUserAuth>
      <Dialog open PaperProps={{ style: { backgroundColor: '#202027' } }}>
        { /* Preload the first video for faster playback */ }
        <video className="hidden" src={replayAssetUrl(`Slide-01-Loop.mp4?tr=q-${VIDEO_QUALITY}`)} autoPlay loop muted playsInline preload="auto" />
        <div className="absolute right-3 top-3">
          <IconButton aria-label="close" onClick={() => router.push('/')}>
            <Close color="primary" />
          </IconButton>
        </div>
        <div className="w-full text-center text-white p-10">
          {isLaunched ? countdownComplete() : countingDown()}
        </div>
      </Dialog>
    </AwaitUserAuth>
  );
};
